import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import styled from "styled-components"
import Image from "@/components/common/Image"
import useEmblaCarousel from "embla-carousel-react"
import useScrollContext from "@/hooks/useScrollContext"
import CommonGallery from "@/components/common/Gallery"
import { BREAKPOINTS, colors } from "@/styles/themeSettings"
import { getCssSizeValue, getMapKey, precise, throttle } from "@/utils/utils"
import IconButton from "@/components/common/Buttons/IconButton"
import { ASSET_TYPE, BUTTON_TYPE, ICONS } from "@/constants"
import { gsap, useGSAP } from "@/utils/gsap"
import TakeOverCursor from "@/components/common/TakeOverCursor"

const Wrapper = styled.div`
	max-width: 100%;
	width: 100%;
	margin-top: ${({ $marginTop }) => ($marginTop ? getCssSizeValue(32, BREAKPOINTS.MOBILE) : 0)};
	margin-bottom: ${({ $marginBottom }) => ($marginBottom ? getCssSizeValue(32, BREAKPOINTS.MOBILE) : 0)};

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		margin-top: ${({ $marginTop }) => ($marginTop ? getCssSizeValue(48, BREAKPOINTS.TABLET) : 0)};
		margin-bottom: ${({ $marginBottom }) => ($marginBottom ? getCssSizeValue(48, BREAKPOINTS.TABLET) : 0)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		margin-top: ${({ $marginTop }) => ($marginTop ? getCssSizeValue(64, BREAKPOINTS.DESKTOP) : 0)};
		margin-bottom: ${({ $marginBottom }) => ($marginBottom ? getCssSizeValue(64, BREAKPOINTS.DESKTOP) : 0)};
	}
`

const GalleryContainer = styled.div`
	position: relative;
	width: 100%;
`

const Embla = styled.div`
	overflow: hidden;
`

const EmblaContainer = styled.div`
	display: flex;
	user-select: none;
	-webkit-touch-callout: none;
	-webkit-tap-highlight-color: transparent;
	--slide-scale: 1;
`

const StyledImage = styled(Image)`
	position: relative;
	cursor: pointer;
	flex: 0 0 80%;
	aspect-ratio: 4/3;
	min-width: 0;
	margin-right: ${getCssSizeValue(8, BREAKPOINTS.MOBILE)};

	img {
		scale: var(--slide-scale, 1);
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		flex: 0 0 40%;
		margin-right: ${getCssSizeValue(16)};
	}

	.image {
		height: 100%;
	}
`

const PlayVideoButton = styled(IconButton)`
	--size: ${getCssSizeValue(50, BREAKPOINTS.MOBILE)};
	position: absolute;
	top: 50%;
	left: 50%;
	translate: -50% -50%;
	background-color: ${({ theme }) => theme.colors.white} !important;
	border-radius: 50%;
	width: var(--size);
	min-height: var(--size);
	padding: ${getCssSizeValue(15, BREAKPOINTS.MOBILE)};

	&:before {
		content: "";
		position: absolute;
		inset: 0;
		background-color: ${({ theme }) => theme.colors.periwinkle} !important;
		border-radius: 50%;
		transform: scale(0);
	}

	.icon {
		height: ${getCssSizeValue(40)};
		translate: 10%;
		transform: scale(1);
		transform-origin: center;
	}

	&:before,
	.icon {
		transition: transform var(--rgr-duration) var(--ease);
	}

	&.hovered {
		&:before {
			transform: scale(1);
		}

		.icon {
			transform: scale(0.8);
		}
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		--size: ${getCssSizeValue(120)};
		padding: ${getCssSizeValue(25)};
	}
`

const DOUBLE_SLIDER_ELEMENTS_THRESHOLD = 4

function formatAssets(data) {
	let source = Array.isArray(data) ? data : []

	if (source.length <= DOUBLE_SLIDER_ELEMENTS_THRESHOLD) {
		source = source.concat(source)
	}

	return source.map((item, index) => ({
		...item,
		id: `${item.id}-${index}`, // unique id formed by appending the index to item's id
		type: item?.customData?.galleryVideo?.length > 0 ? ASSET_TYPE.VIDEO : ASSET_TYPE.IMAGE,
	}))
}

const Slide = ({ data, onMouseUp, onMouseDown }) => {
	const [hover, setHover] = useState(false)
	const isVideo = data?.type === ASSET_TYPE.VIDEO

	return (
		<StyledImage
			src={data.url}
			alt={data?.alt || `gallery ${data?.type || "asset"}`}
			placeholder={data?.blurUpThumb}
			focalPoint={data?.focalPoint}
			onMouseDown={onMouseDown}
			onMouseUp={onMouseUp}
			onPointerEnter={() => {
				setHover(true)
			}}
			onPointerLeave={() => {
				setHover(false)
			}}
		>
			{isVideo && (
				<PlayVideoButton
					noHoverEffect
					icon={ICONS.PLAY}
					color={colors.richBlue}
					type={BUTTON_TYPE.UNSTYLED}
					className={`${hover ? "hovered" : ""}`}
				/>
			)}
		</StyledImage>
	)
}

const Gallery = ({ assets: data, className = "", marginTop = true, marginBottom = true, ...props }) => {
	const [isOpen, setIsOpen] = useState(false)
	const [photoIndex, setPhotoIndex] = useState(0)
	const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, dragFree: true })
	const ContainerRef = useRef(false)
	const isDragging = useRef(false)
	const isScrolling = useRef(false)
	const startPos = useRef({ x: 0, y: 0 })
	const prevScroll = useRef(0)
	const [scale, setScale] = useState(1)
	const assets = useMemo(() => formatAssets(data), [data])
	const scroll = useScrollContext()
	const handleMouseDown = useCallback((event) => {
		startPos.current = { x: event.clientX, y: event.clientY }
	}, [])

	const handleMouseUp = useCallback(
		(index) => (event) => {
			const endPos = { x: event.clientX, y: event.clientY }
			const distance = Math.sqrt(Math.pow(endPos.x - startPos.current.x, 2) + Math.pow(endPos.y - startPos.current.y, 2))
			if (distance < 5) {
				setPhotoIndex(index)
				scroll.lock()
				setIsOpen(true)
			}
		},
		[],
	)

	useEffect(() => {
		const floor = 20
		const multiplier = 10000

		const handleScroll = ({ scrollProgress }) => {
			const scroll = scrollProgress()
			let diff = Math.abs(precise(scroll - prevScroll.current) * multiplier)
			isScrolling.current = diff > 0
			const s = isDragging.current || diff > floor ? 0.85 : 1
			setScale(s)
			prevScroll.current = scroll
		}

		const throttledScroll = throttle(handleScroll, 100)

		const handleUp = ({ scrollProgress }) => {
			isDragging.current = false
			const scroll = scrollProgress()
			let diff = Math.abs(precise(scroll - prevScroll.current) * multiplier)
			const cond = diff > 0
			if (!cond) {
				setScale(1)
			}
			// if(isScrolling.current) return
			// //reset scale if user is not scrolling and stopped dragging
			// setScale(1)
		}
		const handleDown = () => {
			isDragging.current = true
		}

		if (emblaApi) {
			emblaApi.on("scroll", throttledScroll)
			emblaApi.on("pointerDown", handleDown)
			emblaApi.on("pointerUp", handleUp)
		}

		return () => {
			if (emblaApi) {
				emblaApi.off("scroll", throttledScroll)
				emblaApi.off("pointerDown", handleDown)
				emblaApi.off("pointerUp", handleUp)
			}
		}
	}, [emblaApi])

	useGSAP(
		() => {
			gsap.to(ContainerRef.current, {
				"--slide-scale": scale,
			})
		},
		{
			dependencies: [scale],
			scope: ContainerRef.current,
		},
	)

	return (
		<Wrapper className={`${className}`} $marginTop={marginTop} $marginBottom={marginBottom} {...props}>
			<TakeOverCursor Container={GalleryContainer}>
				<CommonGallery
					isOpen={isOpen}
					index={photoIndex}
					slides={assets}
					close={() => {
						setIsOpen(false)
						scroll.unlock(true)
					}}
				/>
				<Embla ref={emblaRef}>
					<EmblaContainer ref={ContainerRef}>
						{assets.map((asset, index) => (
							<Slide
								data={asset}
								key={getMapKey(asset, index)}
								onMouseDown={handleMouseDown}
								onMouseUp={handleMouseUp(index)}
							/>
						))}
					</EmblaContainer>
				</Embla>
			</TakeOverCursor>
		</Wrapper>
	)
}

export default Gallery
